import humanizeString from 'humanize-string'
import React, { FC } from 'react'

import { mainColors } from '../../../../styles'
import { Badge } from '../../../Badge'
import { RatingBar } from '../../../RatingBar'
import type { CategoryReview } from '../../../ReviewCard/ReviewCard.types'
import { LMedium, MLight, MNormal, XSLight, XSMedium, XSNormal } from '../../../Typography'
import { labelByReviewCategory } from '../Review/Review.utils'
import * as S from './DetailedReview.styles'
import { cleanTagText, getBadgeColor } from './DetailedReview.utils'

export interface DetailedReviewProps {
  reviewDetail: Omit<CategoryReview, 'category'> & { category: string }
  shouldUseLargeStyles?: boolean
  hideBadges?: boolean
  className?: string
  shouldShowMaxRating?: boolean
}

export const DetailedReview: FC<DetailedReviewProps> = ({
  className,
  reviewDetail,
  shouldUseLargeStyles,
  hideBadges,
  shouldShowMaxRating = true,
}) => {
  const { category, comment, rating, tags } = reviewDetail
  const tagBadgeColor = getBadgeColor(rating)
  const hasTagOrComment = !!comment || (tags && tags.length > 0)

  const CategoryTitle = shouldUseLargeStyles ? LMedium : XSMedium
  const CommentText = shouldUseLargeStyles ? MNormal : XSNormal
  const NoTagOrCommentText = shouldUseLargeStyles ? MLight : XSLight

  return (
    <S.Container $renderWithLargeGap={shouldUseLargeStyles} className={className}>
      <S.RatingContainer $renderWithSpaceBetween={shouldUseLargeStyles}>
        <CategoryTitle
          color={shouldUseLargeStyles ? mainColors.black : mainColors.neutralExtraDark}
        >
          {labelByReviewCategory[category] ?? humanizeString(category)}
        </CategoryTitle>
        <RatingBar
          rating={rating}
          isSubRating
          shouldHideProgressBar
          shouldRenderRatingAsMediumSize={shouldUseLargeStyles}
          shouldShowMaxRating={shouldShowMaxRating}
        />
      </S.RatingContainer>
      {!hideBadges && tags && tags.length > 0 ? (
        <S.BadgesContainer>
          {tags.map(tag => (
            <Badge key={`${category}-${tag}`} badgeColor={tagBadgeColor}>
              {cleanTagText(tag)}
            </Badge>
          ))}
        </S.BadgesContainer>
      ) : null}
      {comment ? (
        <CommentText color={mainColors.neutralExtremelyDark}>{comment}</CommentText>
      ) : null}
      {!hideBadges && !hasTagOrComment ? (
        <NoTagOrCommentText color={mainColors.neutralExtremelyDark}>
          No tags selected
        </NoTagOrCommentText>
      ) : null}
    </S.Container>
  )
}
